import React, { useRef, useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { UploadCloud, X, Loader2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const AREAS = ["Exterior", "Interior", "Pool area", "Kitchen", "Living room", "Main bedroom", "Bedroom", "Bathroom", "Garden", "Garage", "Other"];

export default function PhotoUploader({ value = [], onChange, label = "Photos", withAreas = false }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [area, setArea] = useState("Exterior");

  const uploadFiles = async (files) => {
    const arr = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (!arr.length) return;
    setBusy(true);
    const added = [];
    for (const f of arr) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const full = `${BACKEND}${data.url}`;
        added.push(withAreas ? { url: full, area } : full);
      } catch (e) {
        toast.error(apiErr(e));
      }
    }
    if (added.length) onChange([...value, ...added]);
    setBusy(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    if (e.dataTransfer.files?.length) uploadFiles(e.dataTransfer.files);
  };

  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <div>
      <div className="lbl-m" style={{ marginBottom: 8 }}>{label}</div>
      {withAreas && (
        <select className="inp" style={{ marginBottom: 10, maxWidth: 240 }} value={area} onChange={(e) => setArea(e.target.value)} data-testid="photo-area-select">
          {AREAS.map((a) => <option key={a}>{a}</option>)}
        </select>
      )}
      <div
        data-testid="photo-dropzone"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        style={{
          border: `1.5px dashed ${drag ? "var(--honey)" : "var(--line)"}`,
          background: drag ? "rgba(176,141,87,.06)" : "var(--soft)",
          borderRadius: 4, padding: "26px 18px", textAlign: "center", cursor: "pointer",
          transition: "all .2s", color: "var(--muted)",
        }}
      >
        {busy ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}><Loader2 size={18} className="spin" /> Uploading…</span>
        ) : (
          <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
            <UploadCloud size={26} strokeWidth={1.4} />
            <span style={{ fontSize: 14 }}>Tap to take a photo or choose from your device</span>
            <span style={{ fontSize: 12 }}>or drag & drop images here</span>
          </span>
        )}
        <input ref={inputRef} type="file" accept="image/*" multiple onChange={(e) => uploadFiles(e.target.files)} style={{ display: "none" }} data-testid="photo-input" />
      </div>

      {value.length > 0 && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 14 }}>
          {value.map((item, i) => {
            const url = typeof item === "string" ? item : item.url;
            const a = typeof item === "string" ? null : item.area;
            return (
            <div key={i} style={{ position: "relative", width: 92, height: 72, borderRadius: 3, overflow: "hidden", border: "1px solid var(--line)" }}>
              <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              {a && <span style={{ position: "absolute", bottom: 0, left: 0, right: 0, fontSize: 8, letterSpacing: ".08em", textTransform: "uppercase", background: "rgba(33,29,24,.7)", color: "#fff", padding: "2px 4px" }}>{a}</span>}
              <button type="button" onClick={() => remove(i)} aria-label="Remove"
                style={{ position: "absolute", top: 4, right: 4, width: 20, height: 20, borderRadius: "50%", background: "rgba(33,29,24,.75)", color: "#fff", border: "none", cursor: "pointer", display: "grid", placeItems: "center" }}>
                <X size={12} />
              </button>
            </div>
          ); })}
        </div>
      )}
      <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
